// angular
import { Meta, Title } from '@angular/platform-browser';
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// components
import { ProductPreviewComponent } from './components/product-preview/product-preview.component';
import { ProductNavigationTabsComponent } from './components/product-navigation-tabs/product-navigation-tabs.component';
import { ProductDescriptionComponent } from './components/product-description/product-description.component';
import { ProductVideosSectionComponent } from './components/product-videos-section/product-videos-section.component';
import { ProductClimaticPropertiesComponent } from './components/product-climatic-properties/product-climatic-properties.component';
import { ProductAdditionalPropertiesComponent } from './components/product-additional-properties/product-additional-properties.component';
import { ProductCatalogsComponent } from './components/product-catalogs/product-catalogs.component';
import { ProductUsageComponent } from './components/product-usage/product-usage.component';
import { ProductExpertOpinionsComponent } from './components/product-expert-opinions/product-expert-opinions.component';
import { ProductCommentsComponent } from './components/product-comments/product-comments.component';
import { FaqSectionComponent } from '@app/core/components/faq-section/faq-section.component';
import { CommentFormBoxComponent } from '@app/core/components/comment-form-box/comment-form-box.component';
import { ProductTagsComponent } from './components/product-tags/product-tags.component';
import { ProductDetailsSkeletonComponent } from './components/product-details-skeleton/product-details-skeleton.component';
import { BreadcrumbsComponent } from '@app/core/components/breadcrumbs/breadcrumbs.component';
import { OptionsMenuComponent } from '@app/core/components/options-menu/options-menu.component';
import { BarcodeDialogComponent } from '@app/core/dialogs/barcode-dialog/barcode-dialog.component';
import { goToPanelSignup } from '@shared/utils/goToExternalUrl';
// services
import { ProductsService } from '../shared/services/products.service';
import { StorageService } from '@app/shared/services/storage.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
import { CommentsService } from '@app/core/shared/services/comments.service';
import { FavoritesService } from '@app/+profile/profile-favorites/shared/favorites.service';
import { UserService } from '@app/shared/services/user.service';
// interfaces & types
import {
  IFaq,
  IProduct,
  PriceType,
  ProductLifeStyle,
} from '../shared/interfaces/product.interface';
import {
  hasProductVideos,
  resolveProductCatalogs,
  resolveProductVideos,
} from '../shared/utils/product-media.util';
import { ISectionTab } from '@app/core/shared/interfaces/section-tab.interface';
import { AdditionalSpecificationType } from '../shared/interfaces/additional-specification.interface';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { IMedia } from '@app/shared/interfaces/media.interface';
import { IBreadcrumbs } from '@app/shared/interfaces/breadcrumbs.interface';
import { ExpertOpinionsType } from '../shared/types/expert-opinions.type';
import { ProductClimaticType } from '../shared/types/product-climatic.type';
import {
  PreviewMenuOptionsType,
  ProductPreviewType,
} from '../shared/types/product-preview.type';
//  enums & env
import { I18n } from '@app/shared/enums/i18n.enum';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
import { environment } from '@environments/environment';
import { resolveMediaFileUrl } from '@app/shared/utils/media.util';
// constants
import { productBreadCrumbs } from '../shared/constants/product-breadcrumbs-items';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'pwj-products-detail',
  standalone: true,
  imports: [
    TranslateModule,
    ProductPreviewComponent,
    ProductTagsComponent,
    ProductNavigationTabsComponent,
    ProductDescriptionComponent,
    ProductVideosSectionComponent,
    ProductClimaticPropertiesComponent,
    ProductAdditionalPropertiesComponent,
    ProductCatalogsComponent,
    ProductUsageComponent,
    ProductExpertOpinionsComponent,
    FaqSectionComponent,
    ProductCommentsComponent,
    CommentFormBoxComponent,
    ProductDetailsSkeletonComponent,
    BreadcrumbsComponent,
    OptionsMenuComponent,
    TranslateDataPipe,
  ],
  templateUrl: './products-detail.component.html',
  styleUrl: './products-detail.component.scss',
})
export class ProductsDetailComponent {
  public breadcrumbsData: IBreadcrumbs[] = [];
  public menuOptions: PreviewMenuOptionsType[] = [];
  public navigationTabsData: ISectionTab[] = [];
  public alreadyAddedToFavorites!: boolean;
  public productPreviewData!: ProductPreviewType;
  public productTags: string[] = [];
  public productDescription!: II18n;
  public productVideoItems: IMedia[] = [];
  public climaticData!: ProductClimaticType;
  public additionalSpecificationData!: AdditionalSpecificationType;
  public usageData: ProductLifeStyle[] = [];
  public expertOpinionsData!: ExpertOpinionsType;
  public faqData!: IFaq[];
  public productDocuments: IMedia[] = [];
  public productCatalogs: IMedia[] = [];
  public hasAdditionalSpecifications = false;
  public hasClimaticData = false;
  public hasVisibleNavigationTabs = false;
  public hasCatalogsAndDocsSection = false;
  public hasLongProductDescription = false;
  public isMobileView = signal(false);
  public activeMobileSection = signal('description');
  public productId!: string;
  public isLoading!: boolean;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;
  public currentLang!: I18n;
  public readonly environment = environment;

  private _userIsLoggedIn!: boolean;

  // dependencies
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _storageService = inject(StorageService);
  private readonly _translateData = inject(TranslateDataPipe);
  private readonly _translateService = inject(TranslateService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _productsService = inject(ProductsService);
  private readonly _titleService = inject(Title);
  private readonly _metaService = inject(Meta);
  private readonly _dialogService = inject(DialogService);
  private readonly _favoritesService = inject(FavoritesService);
  private readonly _userService = inject(UserService);
  private readonly _translateDataPipe = inject(TranslateDataPipe);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  public readonly commentsService = inject(CommentsService);

  constructor() {
    // check user is logged-in or no
    effect(() => {
      this._userIsLoggedIn = !!this._userService.userSignal().username;
    });
    // set current currency
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
    // set product id and load details
    this._activatedRoute.params.subscribe(params => {
      const id = params?.['id'];
      if (!id) {
        this._router.navigate(['']);
        return;
      }

      this.productId = id;
      this._productsService.getProductDetails(id);
    });

    if (this._activatedRoute.snapshot.fragment) {
      void this._router.navigate([], {
        relativeTo: this._activatedRoute,
        fragment: undefined,
        replaceUrl: true,
      });
    }

    this._activatedRoute.fragment.subscribe(fragment => {
      if (fragment) {
        void this._router.navigate([], {
          relativeTo: this._activatedRoute,
          fragment: undefined,
          replaceUrl: true,
        });
      }
    });

    effect(() => {
      // set loading state
      this.isLoading = this._productsService.isProductsDetailsLoading();
    });

    // set current lang
    this.currentLang = (I18n as any)[this._translateService.currentLang];
    this._translateService.onLangChange.subscribe(({ lang }) => {
      this.currentLang = (I18n as any)[lang];

      // update bread crumbs data on lang change
      this.setBreadCrumbsData();
    });

    effect(() => {
      // set bread crumbs data
      this.setBreadCrumbsData();
      // set product details data
      this.getProductDetailsData();
    });

    effect(() => {
      const product = this._productsService.productDetails();
      if (product) {
        this.updateCommentsTabCount();
      }
    });

    afterNextRender(() => {
      if (!isPlatformBrowser(this._platformId)) {
        return;
      }

      const mq = window.matchMedia('(max-width: 47.99em)');
      const syncMobileView = () => this.isMobileView.set(mq.matches);

      syncMobileView();
      mq.addEventListener('change', syncMobileView);
      this._destroyRef.onDestroy(() =>
        mq.removeEventListener('change', syncMobileView),
      );
    });
  }

  public onActiveSectionChange(section: string): void {
    this.activeMobileSection.set(section);
  }

  public isSectionVisible(sectionId: string): boolean {
    if (!this.isMobileView()) {
      return true;
    }

    return this.activeMobileSection() === sectionId;
  }

  public setBreadCrumbsData() {
    const data = this._productsService?.productDetails();

    if (data) {
      // set breadCrumbs data
      const newBreadCrumbs: IBreadcrumbs[] = [
        {
          id: 3,
          breadCrumbTitle: data?.category_type?.title?.[this.currentLang],
          breadCrumbUrl: `/products`,
          breadCrumbParams: {
            category_type_id: data?.category_type?._id,
          },
        },
        {
          id: 4,
          breadCrumbTitle: data?.title?.[this.currentLang] || data?.title?.en,
        },
      ];
      this.breadcrumbsData = [...productBreadCrumbs, ...newBreadCrumbs];
    }
  }

  public getProductDetailsData() {
    // get product details from service

    const data = this._productsService?.productDetails();

    if (data) {
      this.productCatalogs = resolveProductCatalogs(data);
      this.productDocuments = data?.product_documents ?? [];
      this.productVideoItems = resolveProductVideos(data);
      // implement navigation tabs data
      this.implementNavigationTabs(data);
      // is product already liked
      this.alreadyAddedToFavorites = data?.is_favorite;
      this.setMenuOptions();

      // set product preview data (only non-null values passed to preview)
      this.productPreviewData = {
        _id: data?._id,
        business_id: data?.business?._id as string,
        custom_url: data?.business?.custom_url,
        title: data?.title,
        subTitle: data?.category_type?.title as II18n,
        code: data?.code ?? 0,
        businessVerified: !!((data?.business as any)?.is_verified ?? (data?.business as any)?.is_verfied),
        logo: data?.business?.logo as IMedia,
        media: [...(data?.images || []), ...(data?.videos || [])],
        description: data?.description,
        highest_price: data?.highest_price ?? null,
        lowest_price: data?.lowest_price ?? null,
        category_type: data?.category_type?.title,
        category: data?.category?.title,
        sub_category: data?.sub_category?.title ?? undefined,
        stone_grade: data?.stone_grade ?? undefined,
        specialist_verified: !!data?.specialist_verified,
        mobile: data?.business?.mobile?.mobile_number
          ? `${data?.business?.mobile?.prefix ?? ''}${data?.business?.mobile?.mobile_number}`
          : '-',
        is_favorite: data?.is_favorite,
      };
      this.productTags = data?.tags ?? [];
      // set product description data
      this.productDescription = data?.description;
      const descriptionText = this.getProductDescriptionText(data?.description);
      this.hasLongProductDescription = descriptionText.length > 140;
      const meta = data?.metadata;
      // set climatic properties data (only when metadata exists; null values hidden in template)
      this.climaticData = {
        acid_resistance: meta?.acid_resistance ?? '',
        frost_resistance: meta?.frost_resistance ?? '',
        pressure_resistance: meta?.pressure_resistance ?? '',
        water_absorption: meta?.water_absorption ?? '',
        bulk_density: meta?.bulk_density,
        chemical_formula: meta?.chemical_formula ?? '',
      };
      // set additional specification data (nullable fields; template shows only when value present)
      const owner = data?.business?.owner;
      const companyResponsible = owner
        ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || null
        : null;

      this.additionalSpecificationData = {
        company_responsible: companyResponsible,
        warranty: meta?.warranty,
        production_capacity: meta?.production_capacity,
        delivery_address: (data as any)?.delivery_address,
        min_order: data?.min_order ?? undefined,
        available_amount: data?.available_amount ?? undefined,
        delivery_type: data?.delivery_type ?? undefined,
        origin_country: data?.origin_country ? (typeof data.origin_country === 'object' && 'name' in data.origin_country ? (data.origin_country as any).name : data.origin_country) : undefined,
        customizeSize: meta?.customize,
        shipment_terms: data?.shipment_terms ?? undefined,
        payment_methods: data?.payment_methods ?? undefined,
        main_color: meta?.main_color,
        veins: meta?.veins,
        dots: meta?.dots,
        halos: meta?.halos,
        dimensions: meta?.dimensions,
      };
      this.hasClimaticData = !!(
        meta?.acid_resistance ||
        meta?.frost_resistance ||
        meta?.pressure_resistance ||
        meta?.water_absorption ||
        meta?.bulk_density?.value ||
        meta?.chemical_formula
      );
      this.hasAdditionalSpecifications = !!(
        this.additionalSpecificationData.company_responsible ||
        this.additionalSpecificationData.warranty ||
        this.additionalSpecificationData.production_capacity?.value ||
        this.additionalSpecificationData.min_order?.value ||
        this.additionalSpecificationData.available_amount?.value ||
        (this.additionalSpecificationData.shipment_terms?.length ?? 0) > 0 ||
        (this.additionalSpecificationData.payment_methods?.length ?? 0) > 0 ||
        this.additionalSpecificationData.main_color ||
        (this.additionalSpecificationData.veins?.length ?? 0) > 0 ||
        (this.additionalSpecificationData.dots?.length ?? 0) > 0 ||
        (this.additionalSpecificationData.halos?.length ?? 0) > 0 ||
        this.additionalSpecificationData.cutting_face ||
        this.additionalSpecificationData.delivery_type ||
        (this.additionalSpecificationData.dimensions?.length ?? 0) > 0 ||
        this.additionalSpecificationData.origin_country ||
        this.additionalSpecificationData.delivery_address
      );
      // set life style data
      this.usageData = meta?.life_styles ?? [];
      // set expert opinions data
      this.expertOpinionsData = {
        specialist_person: data?.specialist_person ?? undefined,
        business_title: data?.business?.title as II18n,
        specialist_note: data?.specialist_note,
        specialist_video: data?.specialist_video ?? [],
        specialist_photos: data?.specialist_photos ?? [],
      };
      // set faq q&a data
      this.faqData = data?.faq ?? [];
      const productTitle = `${this._translateService.instant('routes.productDetails')} ${this._translateData.transform(data?.title) || ''}`;
      const productDescription = data.description?.['en'];
      const productImage =
        environment.baseUrl.file +
        (data?.main_photo?.cropped_url?.toLowerCase() ||
          data?.images?.[0]?.cropped_url?.toLowerCase() ||
          data?.images?.[0]?.url);

      this.setPageTitle(productTitle);
      this.setMetaTags(productTitle, productDescription, productImage);
      this._changeDetectorRef.markForCheck();
    }
  }

  public setPageTitle(content: string) {
    if (content) {
      this._titleService.setTitle(content);
    }
  }

  public setMetaTags(
    productTitle: string,
    productDescription?: string,
    productImage?: string
  ) {
    if (productTitle) {
      this._metaService.updateTag({
        property: 'og:title',
        content: productTitle || 'title',
      });
    }

    if (productDescription) {
      this._metaService.updateTag({
        property: 'og:description',
        content: productDescription,
      });
    }

    if (productImage) {
      this._metaService.updateTag({
        property: 'og:image',
        content: productImage,
      });
    }
    if (window?.location?.href) {
      this._metaService.updateTag({
        property: 'og:url',
        content: window.location.href,
      });
    }
  }

  public addProductToFavorite() {
    if (this._userIsLoggedIn) {
      if (this.productPreviewData._id) {
        if (!this.alreadyAddedToFavorites) {
          // set favorite mode
          this.alreadyAddedToFavorites = true;
        } else {
          // set favorite mode
          this.alreadyAddedToFavorites = false;
        }

        this.setMenuOptions();

        // call add to favorite service
        this._favoritesService.addProductToFavorite(
          this.productPreviewData._id
        );
      }
    } else {
      goToPanelSignup();
    }
  }

  public openQrCodeDialog(barcodeContent: string) {
    this._dialogService.open(BarcodeDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'products.qrCode',
        productTitle: this.productPreviewData.title?.['en'],
        productCode: this.productPreviewData.code,
        barcodeContent,
      },
    });
  }

  public setMenuOptions() {
    // implement menu options data
    this.menuOptions = [
      {
        id: 0,
        name: 'products.favorite',
        optionClasses:
          '!flex-row gap-6 !text-2xl menu-options__item--favorite-action',
        icon: !this.alreadyAddedToFavorites ? 'heart' : null,
        imgIcon: this.alreadyAddedToFavorites ? 'assets/icons/heart.svg' : null,
        imgWidth: '20',
        imgHeight: '20',
        onSelectOption: () => {
          this.addProductToFavorite();
        },
      },
      {
        id: 1,
        name: 'global.share',
        optionClasses:
          '!flex-row gap-6 !text-2xl menu-options__item--share-action',
        icon: 'share',
        onSelectOption: (barcodeContent: string) => {
          this.openQrCodeDialog(barcodeContent);
        },
      },
    ];
  }

  public productDocumentHref(doc: IMedia): string {
    const url = doc?.url;
    if (!url) {
      return '#';
    }
    return resolveMediaFileUrl(url, environment.baseUrl.file) || '#';
  }

  /** Label shown on the card (matches person-detail: strip leading numeric upload prefix). */
  public productDocumentDisplayName(doc: IMedia): string {
    const raw = doc?.name?.trim();
    const fromName = raw ? raw.replace(/^\d+-/, '').trim() || raw : '';
    if (fromName) {
      return fromName;
    }
    const url = doc?.url?.trim();
    if (!url) {
      return '';
    }
    const segment = url.split('?')[0].split('/').pop() ?? '';
    const stripped = segment.replace(/^\d+-/, '').trim();
    return stripped || segment;
  }

  public getProductDescriptionText(description?: II18n | null): string {
    const text = (this._translateDataPipe.transform(description) ?? '').trim();
    return text === '-' ? '' : text;
  }

  public implementNavigationTabs(product: IProduct) {
    this.hasCatalogsAndDocsSection = !!(
      this.hasAdditionalSpecifications ||
      this.productCatalogs?.length ||
      this.productDocuments?.length
    );

    this.navigationTabsData = [
      {
        tabTitle: 'description',
        iconName: 'note-text',
        hiddenTab: !this.hasLongProductDescription,
      },
      {
        tabTitle: 'specifications',
        iconName: 'task-list',
        anchorId: 'additionalSpecifications',
        hiddenTab: !this.hasAdditionalSpecifications,
      },
      {
        tabTitle: 'catalogsAndDocs',
        iconName: 'document-text-1',
        hiddenTab: !this.hasCatalogsAndDocsSection,
      },
      {
        tabTitle: 'videos',
        iconName: 'play-circle',
        hiddenTab: !hasProductVideos(product),
      },
      {
        tabTitle: 'comments',
        iconName: 'messages-2',
        tabDataCount: this.getCommentsCount(),
      },
    ];

    this.updateCommentsTabCount();

    this.hasVisibleNavigationTabs = this.navigationTabsData.some(
      tab => !tab.hiddenTab,
    );

    const firstVisibleTab = this.navigationTabsData.find(tab => !tab.hiddenTab);
    if (firstVisibleTab) {
      this.activeMobileSection.set(firstVisibleTab.tabTitle);
    }
  }

  private getCommentsCount(): number {
    return this.commentsService.commentsData()?.length ?? 0;
  }

  private updateCommentsTabCount() {
    const count = this.getCommentsCount();

    this.navigationTabsData = this.navigationTabsData.map(tab =>
      tab.tabTitle === 'comments' ? { ...tab, tabDataCount: count } : tab,
    );
  }
}
